import type { Call, Device } from '@twilio/voice-sdk'
import { dialableNanp, nanpNumber } from '../functions/lib/phone-plan'
import { getMe } from './me'
import { callingCode, phoneClock } from './round-config'
import { dayLabel, el, toast } from './ui'

// Calls inside Roundly (0027, Liam 2026-09-26: "I want the full"). For owners
// of the business calling is set up for (/api/me says calling: true):
//   - every phone link in Roundly rings the customer from inside Roundly, and
//     the customer sees the Dropby work number;
//   - a customer ringing the work number that nobody answers there rings
//     Roundly: in the iPhone app like a normal call (ios/Roundly/Calls), on a
//     computer while Roundly is open;
//   - missed calls wait in a bar under the top of the screen with Call back;
//   - the customer page lists that customer's calls.
// Everyone else keeps ordinary phone links. Twilio's calling library is only
// downloaded the first time it is needed.

interface RoundlyApp {
  call(method: string, params?: unknown): Promise<unknown>
}

function roundlyApp(): RoundlyApp | null {
  if (typeof window === 'undefined') return null
  const app = (window as unknown as { RoundlyIOS?: RoundlyApp }).RoundlyIOS
  return app && typeof app.call === 'function' ? app : null
}

// The iPhone app answers calls itself when this build of it can (app.info
// says calls: true). An older build falls back to calling from the page.
let nativeChecked: RoundlyApp | null | undefined
// Which of Apple's push services this app build uses: a Debug build on the
// test one needs Twilio's matching push credential.
let pushEnvironment: 'production' | 'sandbox' = 'production'
async function nativeCalls(): Promise<RoundlyApp | null> {
  if (nativeChecked !== undefined) return nativeChecked
  const app = roundlyApp()
  if (!app) return (nativeChecked = null)
  try {
    const info = (await app.call('app.info')) as { calls?: boolean; pushEnvironment?: string } | null
    nativeChecked = info?.calls === true ? app : null
    if (info?.pushEnvironment === 'sandbox') pushEnvironment = 'sandbox'
  } catch {
    nativeChecked = null
  }
  return nativeChecked
}

export function callingOn(): boolean {
  return getMe()?.calling === true
}

function businessHeader(): Record<string, string> {
  const id = getMe()?.businessId
  return typeof id === 'number' ? { 'X-Roundly-Business': String(id) } : {}
}

async function send(path: string, init: RequestInit = {}): Promise<Response | null> {
  try {
    return await fetch(path, { ...init, headers: { ...(init.body ? { 'content-type': 'application/json' } : {}), ...businessHeader() }, credentials: 'same-origin', signal: AbortSignal.timeout(15_000) })
  } catch {
    return null
  }
}

// The pass this phone or computer calls with (/api/calls/token). register:
// ring here when a customer calls.
async function pass(register: boolean, platform: 'ios' | 'web'): Promise<string | null> {
  const res = await send('/api/calls/token', { method: 'POST', body: JSON.stringify(platform === 'ios' ? { register, platform, pushEnvironment } : { register, platform }) })
  if (!res?.ok) return null
  const body = (await res.json().catch(() => null)) as { token?: unknown } | null
  return typeof body?.token === 'string' ? body.token : null
}

// "07700 900123 (Anna)" → "+447700900123". The server checks it again and
// refuses anything but an ordinary UK number. A US or Canadian round reads "(512) 555-0142" as "+15125550142" with the
// server's own rule (functions/lib/twilio.ts nanpNumber; Step 2 round 3).
export function callNumber(raw: string): string | null {
  if (callingCode() === '1') return nanpNumber(raw)
  const m = raw.match(/(?:\+|00)?[\d\s().-]{9,}/)
  if (!m) return null
  const plus = /^\s*(\+|00)/.test(m[0])
  let digits = m[0].replace(/\D/g, '')
  if (plus && digits.startsWith('00')) digits = digits.slice(2)
  if (digits.startsWith('44')) digits = digits.slice(2)
  else if (digits.startsWith('0')) digits = digits.slice(1)
  else if (plus) return null
  if (digits.startsWith('0')) digits = digits.slice(1)
  return digits.length === 9 || digits.length === 10 ? `+44${digits}` : null
}

// The numbers a call from Roundly may ring: the server's own rule
// (functions/lib/twilio.ts dialable). Anything else keeps the phone's own
// dialler, so no customer becomes unreachable.
export function dialable(e164: string): boolean {
  if (callingCode() === '1') return dialableNanp(e164)
  if (/^\+44(1481|1534|1624|7509|7781|7797|7829|7839|7911|7937)/.test(e164)) return false
  return /^\+44(1\d{8,9}|2\d{9}|3\d{9}|7[1-57-9]\d{8}|80[08]\d{6,7})$/.test(e164)
}

// The phone's own dialler, for when a call from Roundly can't be made.
function ordinaryCall(e164: string): void {
  window.location.href = `tel:${e164}`
}

// "+447700900999" → "07700 900999", "+442079460999" → "020 7946 0999", "+15125550142" → "(512) 555-0142".
export function shown(e164: string): string {
  const us = /^\+1(\d{3})(\d{3})(\d{4})$/.exec(e164)
  if (us) return `(${us[1]}) ${us[2]}-${us[3]}`
  if (!e164.startsWith('+44')) return e164
  const n = `0${e164.slice(3)}`
  if (/^0(7\d{3}|1\d{3}|800|808)\d{6}$/.test(n)) return `${n.slice(0, 5)} ${n.slice(5)}`
  if (/^0[23]\d{9}$/.test(n)) return `${n.slice(0, 3)} ${n.slice(3, 7)} ${n.slice(7)}`
  return n
}

// "the Dropby number": the business's own short name, as in its messages.
const workNumber = (): string => `the ${getMe()?.settings?.short_name?.trim() || 'work'} number`

// ---- the call screen (computers, and an iPhone app too old to call itself) ----

let device: Device | null = null
let deviceStarting: Promise<Device | null> | null = null
let busy = false

function webDevice(): Promise<Device | null> {
  if (device) return Promise.resolve(device)
  deviceStarting ??= (async () => {
    const token = await pass(true, 'web')
    if (!token) return null
    const { Device } = await import('@twilio/voice-sdk')
    const d = new Device(token, { closeProtection: 'A call is still going. Leave Roundly anyway?' })
    d.on('tokenWillExpire', () => void pass(true, 'web').then((t) => t && d.updateToken(t)))
    d.on('incoming', (call: Call) => ringing(call))
    d.on('error', (err: { message?: string }) => console.warn('calls:', err?.message))
    try {
      await d.register()
    } catch {
      // Calling out still works; this computer just won't ring.
    }
    device = d
    return d
  })().finally(() => {
    deviceStarting = null
  })
  return deviceStarting
}

interface Screen {
  root: HTMLElement
  status: HTMLElement
  actions: HTMLElement
  close: () => void
}

function openScreen(who: string, detail: string, status: string): Screen {
  busy = true
  const statusEl = el('p', { class: 'call-status', role: 'status' }, status)
  const actions = el('div', { class: 'call-actions' })
  const root = el(
    'div',
    { class: 'call-screen', role: 'dialog', 'aria-label': `Call with ${who}` },
    el('p', { class: 'call-who' }, who),
    el('p', { class: 'call-detail' }, detail),
    statusEl,
    actions,
  )
  document.body.append(root)
  let closed = false
  return {
    root,
    status: statusEl,
    actions,
    close: () => {
      if (closed) return
      closed = true
      busy = false
      root.remove()
      window.dispatchEvent(new CustomEvent('roundly:calls-changed'))
    },
  }
}

function button(label: string, cls: string, onClick: () => void): HTMLButtonElement {
  const b = el('button', { type: 'button', class: `call-btn ${cls}` }, label)
  b.addEventListener('click', onClick)
  return b
}

// Mute, Hang up and the running time, once a call is connected or dialling.
function talking(call: Call, screen: Screen, dialling: boolean): void {
  let started = 0
  let tick: number | undefined
  const mute = button('Mute', 'call-mute', () => {
    call.mute(!call.isMuted())
    mute.textContent = call.isMuted() ? 'Unmute' : 'Mute'
    mute.setAttribute('aria-pressed', String(call.isMuted()))
  })
  const hangUp = button('Hang up', 'call-hangup', () => call.disconnect())
  screen.actions.replaceChildren(mute, hangUp)
  const end = (words: string | null): void => {
    window.clearInterval(tick)
    screen.close()
    if (words) toast(words)
  }
  const connected = (): void => {
    if (started) return
    started = Date.now()
    const show = (): void => {
      const s = Math.floor((Date.now() - started) / 1000)
      screen.status.textContent = `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`
    }
    show()
    tick = window.setInterval(show, 1000)
  }
  if (dialling) call.on('ringing', () => (screen.status.textContent = 'Ringing…'))
  else screen.status.textContent = 'Connecting…'
  call.on('accept', () => connected())
  call.on('disconnect', () => end(started ? 'Call ended.' : null))
  call.on('cancel', () => end(null))
  call.on('reject', () => end(null))
  call.on('error', (err: { message?: string }) => end(`The call dropped${err?.message ? `: ${err.message}` : ''}.`))
}

async function webCall(to: string, who: string): Promise<void> {
  if (busy) {
    toast('A call is already going.')
    return
  }
  const screen = openScreen(who, shown(to), `Calling from ${workNumber()}…`)
  screen.actions.replaceChildren(button('Cancel', 'call-hangup', () => screen.close()))
  let d: Device | null = null
  try {
    d = await webDevice()
  } catch {
    d = null
  }
  if (!screen.root.isConnected) return
  if (!d) {
    // No pass or no calling library: ring from the phone itself instead.
    screen.close()
    toast("Couldn't call from Roundly, so your phone's own dialler is opening.")
    ordinaryCall(to)
    return
  }
  try {
    const call = await d.connect({ params: { To: to } })
    // Cancelled while the call was being set up: hang it up at once.
    if (!screen.root.isConnected) {
      call.disconnect()
      return
    }
    talking(call, screen, true)
  } catch {
    screen.close()
    toast("Couldn't start the call. Allow the microphone for Roundly and try again.")
  }
}

// A customer ringing this computer.
function ringing(call: Call): void {
  if (busy) {
    call.reject()
    return
  }
  const who = call.customParameters.get('customer') ?? 'A customer'
  const screen = openScreen(who, `Calling ${workNumber()}`, 'Incoming call')
  const answer = button('Answer', 'call-answer', () => {
    call.accept()
    talking(call, screen, false)
  })
  const decline = button('Decline', 'call-hangup', () => {
    call.reject()
    screen.close()
  })
  screen.actions.replaceChildren(answer, decline)
  call.on('cancel', () => screen.close())
}

// ---- starting a call ------------------------------------------------------------

export async function startCall(raw: string, who: string): Promise<void> {
  const to = callNumber(raw)
  if (!to) {
    toast("That number can't be called from Roundly.")
    return
  }
  const app = await nativeCalls()
  if (!app) return webCall(to, who)
  const token = await pass(false, 'ios')
  if (!token) {
    toast("Couldn't call from Roundly, so your phone's own dialler is opening.")
    ordinaryCall(to)
    return
  }
  try {
    await app.call('calls.start', { token, to, name: who.slice(0, 80) })
  } catch (err) {
    toast(err instanceof Error && err.message ? err.message : "Couldn't start the call.")
  }
}

// Every phone link in Roundly (they are all built by telHref in ui.ts) rings
// from inside Roundly for someone who can call.
function onPhoneLink(e: MouseEvent): void {
  if (!callingOn() || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return
  const link = (e.target as Element | null)?.closest?.('a[href^="tel:"]') as HTMLAnchorElement | null
  if (!link) return
  const number = decodeURIComponent(link.getAttribute('href')!.slice(4))
  const e164 = callNumber(number)
  // A foreign, premium or island number: the phone's own dialler, as before.
  if (!e164 || !dialable(e164)) return
  e.preventDefault()
  const text = link.textContent?.trim() ?? ''
  const named = link.dataset.callName || link.getAttribute('aria-label')?.replace(/^call\s+/i, '') || (/[a-z]/i.test(text) && !/^call$/i.test(text) ? text : '')
  void startCall(number, named || shown(callNumber(number) ?? number))
}

// ---- missed calls ------------------------------------------------------------------

interface CallRow {
  id: number
  direction: 'in' | 'out'
  number: string
  customer_id: number | null
  customer_name?: string | null
  status: string
  duration_seconds: number | null
  created_at: string
  who?: string | null
}

const when = (utc: string): string => {
  const d = new Date(`${utc.replace(' ', 'T')}Z`)
  const clock = phoneClock(d)
  const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  const today = new Date()
  const isToday = d.toDateString() === today.toDateString()
  return isToday ? clock : `${dayLabel(iso)} ${clock}`
}

async function markSeen(id: number): Promise<void> {
  await send(`/api/calls/${id}/seen`, { method: 'POST', body: '{}' })
}

async function refreshMissed(): Promise<void> {
  const res = await send('/api/calls?missed=1')
  if (!res?.ok) return
  const body = (await res.json().catch(() => null)) as { calls?: CallRow[] } | null
  const calls = body?.calls ?? []
  document.querySelector('#missed-calls')?.remove()
  if (calls.length === 0) return
  const bar = el('section', { id: 'missed-calls', class: 'missed-calls', 'aria-label': 'Missed calls' })
  for (const c of calls.slice(0, 3)) {
    const who = c.customer_name || shown(c.number)
    const row = el('div', { class: 'missed-call' }, el('span', { class: 'missed-call-words' }, `Missed call from ${who} · ${when(c.created_at)}`))
    if (c.number !== 'withheld') {
      row.append(button('Call back', 'missed-call-back', () => {
        void markSeen(c.id).then(refreshMissed)
        void startCall(c.number, who)
      }))
    }
    const dismiss = button('✕', 'missed-call-dismiss', () => {
      row.remove()
      void markSeen(c.id).then(refreshMissed)
    })
    dismiss.setAttribute('aria-label', `Dismiss the missed call from ${who}`)
    row.append(dismiss)
    bar.append(row)
  }
  if (calls.length > 3) bar.append(el('p', { class: 'missed-call-more' }, `and ${calls.length - 3} more`))
  const top = document.querySelector('header.topbar')
  if (top) top.after(bar)
}

// ---- the customer page -----------------------------------------------------------

function callLine(c: CallRow): string {
  const who = c.who || 'Someone'
  const length = c.duration_seconds ? ` · ${Math.floor(c.duration_seconds / 60)}:${String(c.duration_seconds % 60).padStart(2, '0')}` : ''
  if (c.direction === 'out') {
    return c.status === 'completed' ? `${who} rang them${length}` : c.status === 'ringing' ? `${who} rang them` : `${who} rang, no answer`
  }
  if (c.status === 'missed') return 'They rang, missed'
  return c.status === 'ringing' ? 'They rang' : `They rang, ${who} answered${length}`
}

export function customerCalls(customerId: number): HTMLElement | null {
  if (!callingOn()) return null
  const box = el('section', { class: 'customer-calls', 'aria-label': 'Calls' })
  const load = async (): Promise<void> => {
    const res = await send(`/api/calls?customer=${customerId}`)
    if (!res?.ok) return
    const body = (await res.json().catch(() => null)) as { calls?: CallRow[] } | null
    const calls = body?.calls ?? []
    box.replaceChildren()
    if (calls.length === 0) return
    box.append(el('h3', { class: 'customer-calls-title' }, 'Calls'))
    const list = el('ul', { class: 'customer-calls-list' })
    for (const c of calls.slice(0, 5)) list.append(el('li', { class: c.status === 'missed' ? 'call-missed' : '' }, `${callLine(c)} · ${when(c.created_at)}`))
    box.append(list)
  }
  void load()
  const again = (): void => {
    if (box.isConnected) void load()
    else window.removeEventListener('roundly:calls-changed', again)
  }
  window.addEventListener('roundly:calls-changed', again)
  return box
}

// ---- once, after sign-in (main.ts) -------------------------------------------------

let watching = false

async function ringHere(): Promise<void> {
  const app = await nativeCalls()
  if (app) {
    const token = await pass(true, 'ios')
    if (token) await app.call('calls.register', { token }).catch(() => undefined)
    return
  }
  // An iPhone app too old to take calls itself would only ring while open:
  // it calls out from the page, but does not claim to take calls.
  if (roundlyApp()) return
  void webDevice()
}

export function watchCalls(): void {
  if (watching || !callingOn()) return
  watching = true
  document.addEventListener('click', onPhoneLink, true)
  void ringHere()
  void refreshMissed()
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void refreshMissed()
  })
  window.addEventListener('roundly:calls-changed', () => void refreshMissed())
  window.setInterval(() => {
    if (document.visibilityState === 'visible') void refreshMissed()
  }, 60_000)
}

// Log out: this phone or computer stops ringing (the server also stops
// ringing a signed-out phone; this lets go of Twilio's side too).
export async function forgetCallsHere(): Promise<void> {
  const app = await nativeCalls()
  if (app) await app.call('calls.unregister').catch(() => undefined)
  device?.destroy()
  device = null
}
